// ---------------------------------------------------------------------------
// F-076 Phase 2 — Competitor Monitoring CQRS event catalog + pure builders
// ---------------------------------------------------------------------------
// Story F-076: "Competitor monitoring tracks competitor business profiles
// across public review/listing platforms (Google, Yelp, Facebook), detects
// newly published reviews per competitor, computes a head-to-head benchmark
// between the tenant and tracked competitors, and fires an alert when the
// benchmark delta crosses a configured threshold."
//
// Per `docs/vision_execution/.../FEATURE-REGISTRY` audit (status OPEN): no
// surface on `origin/main` emits competitor-monitoring lifecycle events yet.
// This Phase 2 narrow follow-up pins the CQRS vocabulary the future
// monitoring engine, polling adapter, benchmark projector, and alert router
// MUST import so the four events cannot drift across:
//
//   - the competitor-profile polling worker (Phase 3),
//   - the review-detection ingester (Phase 3),
//   - the benchmark materialiser (Phase 4),
//   - the alert router that fans the threshold-exceeded event out to email,
//     in-product notifications, and the operator dashboard (Phase 5).
//
// Sibling catalogs and how F-076 differs (drift guard):
//   - `rv-013-competitor-intelligence-event-catalog` uses the
//     `reputation.competitor_*` namespace and is scoped to the reputation
//     product's snapshot + benchmark MV (Google Places only). F-076 ships
//     under the `competitor.*` namespace because it is product-agnostic
//     (multi-source: google | yelp | facebook) and is consumed by the
//     monitoring engine + alert router, NOT by the reputation MV.
//   - `soc-009-competitor-tracking-event-catalog` uses
//     `competitor.observation@v1` / `competitor.movement_detected@v1` and
//     models social-side observations (posts, ad creatives, engagement
//     counters). F-076 is review-side: per-profile tracking, per-review
//     detection, head-to-head benchmark, threshold alerts. The two
//     vocabularies intentionally do not overlap.
//
// Pure builders only. No I/O. No DB writes. No env access. The writer
// (polling worker route or detection ingester) owns actual emission to
// `customer_events` + the `event-router` worker — mirrors the BIL-017 /
// AN-001 / MKT-001 Phase 3 pattern.
//
// What this file is NOT:
//   - It does not implement the polling adapter (Phase 3).
//   - It does not declare new HTTP routes or new DB tables.
//   - It does not emit events — it pins the contract those emits MUST honour.
// ---------------------------------------------------------------------------

import { z } from "zod";

/* -------------------------------------------------------------------------- */
/* Event type catalog — frozen string constants                               */
/* -------------------------------------------------------------------------- */

export const F_076_COMPETITOR_PROFILE_TRACKED_EVENT =
  "competitor.profile_tracked@v1" as const;
export const F_076_COMPETITOR_REVIEW_DETECTED_EVENT =
  "competitor.review_detected@v1" as const;
export const F_076_COMPETITOR_BENCHMARK_COMPUTED_EVENT =
  "competitor.benchmark_computed@v1" as const;
export const F_076_COMPETITOR_ALERT_THRESHOLD_EXCEEDED_EVENT =
  "competitor.alert_threshold_exceeded@v1" as const;

/**
 * Closed catalog of F-076 competitor-monitoring CQRS event types. Versioned
 * with `@v1` suffix per Governance Rule #7 — future shape changes ship `@v2`
 * alongside, never mutate in place. One business fact per event
 * (Governance Rule #6).
 *
 *   - `competitor.profile_tracked@v1`           — tenant attached a new
 *     competitor profile on a specific public source.
 *   - `competitor.review_detected@v1`           — the polling adapter
 *     observed a new public review on a tracked competitor profile.
 *   - `competitor.benchmark_computed@v1`        — the benchmark projector
 *     recomputed the head-to-head score for a tracked competitor.
 *   - `competitor.alert_threshold_exceeded@v1`  — the benchmark delta moved
 *     past the configured threshold in either direction.
 */
export const F_076_COMPETITOR_MONITORING_EVENT_TYPES = [
  F_076_COMPETITOR_PROFILE_TRACKED_EVENT,
  F_076_COMPETITOR_REVIEW_DETECTED_EVENT,
  F_076_COMPETITOR_BENCHMARK_COMPUTED_EVENT,
  F_076_COMPETITOR_ALERT_THRESHOLD_EXCEEDED_EVENT,
] as const;

export type F076CompetitorMonitoringEventType =
  (typeof F_076_COMPETITOR_MONITORING_EVENT_TYPES)[number];

/* -------------------------------------------------------------------------- */
/* Closed sub-vocabularies                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Closed catalog of public sources F-076 may track a competitor profile on.
 * Pinned to the three sources called out in the story headline so the
 * polling worker, the detection ingester, and the dashboard filter stay in
 * lockstep. Append-only — new sources require a fresh entry + drift snapshot
 * bump.
 *
 *   - `google`    — Google Business Profile (Places API).
 *   - `yelp`      — Yelp Fusion API.
 *   - `facebook`  — Facebook Page reviews / recommendations.
 */
export const F_076_COMPETITOR_SOURCE_VOCAB = [
  "google",
  "yelp",
  "facebook",
] as const;

export type F076CompetitorSource = (typeof F_076_COMPETITOR_SOURCE_VOCAB)[number];

/**
 * Closed catalog of platforms a detected review may originate from. Mirrors
 * `F_076_COMPETITOR_SOURCE_VOCAB` because a review can only be detected on
 * a platform the competitor profile is tracked on. Kept as a separate
 * constant (not a re-export) so future divergence (e.g. cross-posted Yelp
 * reviews reflected on Google) does not have to mutate the source vocab.
 */
export const F_076_COMPETITOR_REVIEW_PLATFORM_VOCAB = [
  "google",
  "yelp",
  "facebook",
] as const;

export type F076CompetitorReviewPlatform =
  (typeof F_076_COMPETITOR_REVIEW_PLATFORM_VOCAB)[number];

/**
 * Closed catalog of directions the benchmark delta may cross the configured
 * threshold in. Pinned so the alert router can `switch` exhaustively without
 * any free-form string drift across email + in-product + dashboard rendering.
 *
 *   - `tenant_ahead`     — tenant score moved above competitor score by at
 *     least the configured basis-point delta.
 *   - `competitor_ahead` — competitor score moved above tenant score by at
 *     least the configured basis-point delta.
 */
export const F_076_COMPETITOR_ALERT_DIRECTION_VOCAB = [
  "tenant_ahead",
  "competitor_ahead",
] as const;

export type F076CompetitorAlertDirection =
  (typeof F_076_COMPETITOR_ALERT_DIRECTION_VOCAB)[number];

/* -------------------------------------------------------------------------- */
/* Runtime narrowing guards                                                   */
/* -------------------------------------------------------------------------- */

export function isF076CompetitorMonitoringEventType(
  value: unknown,
): value is F076CompetitorMonitoringEventType {
  return (
    typeof value === "string" &&
    (F_076_COMPETITOR_MONITORING_EVENT_TYPES as readonly string[]).includes(
      value,
    )
  );
}

export function isF076CompetitorSource(
  value: unknown,
): value is F076CompetitorSource {
  return (
    typeof value === "string" &&
    (F_076_COMPETITOR_SOURCE_VOCAB as readonly string[]).includes(value)
  );
}

export function isF076CompetitorReviewPlatform(
  value: unknown,
): value is F076CompetitorReviewPlatform {
  return (
    typeof value === "string" &&
    (F_076_COMPETITOR_REVIEW_PLATFORM_VOCAB as readonly string[]).includes(
      value,
    )
  );
}

export function isF076CompetitorAlertDirection(
  value: unknown,
): value is F076CompetitorAlertDirection {
  return (
    typeof value === "string" &&
    (F_076_COMPETITOR_ALERT_DIRECTION_VOCAB as readonly string[]).includes(
      value,
    )
  );
}

/* -------------------------------------------------------------------------- */
/* Per-event payload schemas (Zod) + TypeScript types                         */
/* -------------------------------------------------------------------------- */

/**
 * Maximum length for the free-form competitor display label captured at
 * tracking time. Bounded so the event log cannot be inflated by an unbounded
 * provider-side string.
 */
export const F_076_COMPETITOR_DISPLAY_LABEL_MAX_LENGTH = 256;

/**
 * Maximum length for the trimmed inbound review excerpt the detector
 * captures. Detection events carry a short excerpt for operator triage but
 * the full review body lives in the detection store, not in the event log.
 */
export const F_076_REVIEW_EXCERPT_MAX_LENGTH = 512;

/**
 * Minimum rating value the detector accepts. Mirrors the universal review
 * scale (1-5 inclusive) across Google / Yelp / Facebook public reviews.
 */
export const F_076_REVIEW_RATING_MIN = 1;
export const F_076_REVIEW_RATING_MAX = 5;

/**
 * Bounds for `deltaBps` (basis points) on the benchmark event. 100 bps = 1
 * percentage point. The benchmark scale is 0-10_000 bps (0-100%) absolute,
 * so the signed delta is bounded by [-10_000, 10_000].
 */
export const F_076_BENCHMARK_DELTA_BPS_MIN = -10_000;
export const F_076_BENCHMARK_DELTA_BPS_MAX = 10_000;

/**
 * Bounds for `myScore` / `competitorScore`. The benchmark projector emits
 * scores on a 0-10_000 bps scale (basis points of the rolling weighted
 * benchmark) so downstream consumers do not have to negotiate decimal vs
 * percent representation.
 */
export const F_076_BENCHMARK_SCORE_MIN = 0;
export const F_076_BENCHMARK_SCORE_MAX = 10_000;

export const F076CompetitorProfileTrackedPayloadSchema = z.object({
  type: z.literal(F_076_COMPETITOR_PROFILE_TRACKED_EVENT),
  tenantId: z.string().min(1),
  competitorId: z.string().min(1),
  sources: z.array(z.enum(F_076_COMPETITOR_SOURCE_VOCAB)).min(1),
  displayLabel: z
    .string()
    .min(1)
    .max(F_076_COMPETITOR_DISPLAY_LABEL_MAX_LENGTH),
  actorUserId: z.string().min(1),
  occurredAtIso: z.string().datetime({ offset: true }),
  correlationId: z.string().min(1),
});
export type F076CompetitorProfileTrackedPayload = z.infer<
  typeof F076CompetitorProfileTrackedPayloadSchema
>;

export const F076CompetitorReviewDetectedPayloadSchema = z.object({
  type: z.literal(F_076_COMPETITOR_REVIEW_DETECTED_EVENT),
  tenantId: z.string().min(1),
  competitorId: z.string().min(1),
  platform: z.enum(F_076_COMPETITOR_REVIEW_PLATFORM_VOCAB),
  providerReviewId: z.string().min(1),
  rating: z
    .number()
    .int()
    .min(F_076_REVIEW_RATING_MIN)
    .max(F_076_REVIEW_RATING_MAX),
  excerpt: z.string().max(F_076_REVIEW_EXCERPT_MAX_LENGTH).nullable(),
  reviewedAtIso: z.string().datetime({ offset: true }),
  detectedAtIso: z.string().datetime({ offset: true }),
  occurredAtIso: z.string().datetime({ offset: true }),
  correlationId: z.string().min(1),
});
export type F076CompetitorReviewDetectedPayload = z.infer<
  typeof F076CompetitorReviewDetectedPayloadSchema
>;

export const F076CompetitorBenchmarkComputedPayloadSchema = z.object({
  type: z.literal(F_076_COMPETITOR_BENCHMARK_COMPUTED_EVENT),
  tenantId: z.string().min(1),
  competitorId: z.string().min(1),
  myScore: z
    .number()
    .int()
    .min(F_076_BENCHMARK_SCORE_MIN)
    .max(F_076_BENCHMARK_SCORE_MAX),
  competitorScore: z
    .number()
    .int()
    .min(F_076_BENCHMARK_SCORE_MIN)
    .max(F_076_BENCHMARK_SCORE_MAX),
  deltaBps: z
    .number()
    .int()
    .min(F_076_BENCHMARK_DELTA_BPS_MIN)
    .max(F_076_BENCHMARK_DELTA_BPS_MAX),
  computedAtIso: z.string().datetime({ offset: true }),
  occurredAtIso: z.string().datetime({ offset: true }),
  correlationId: z.string().min(1),
});
export type F076CompetitorBenchmarkComputedPayload = z.infer<
  typeof F076CompetitorBenchmarkComputedPayloadSchema
>;

export const F076CompetitorAlertThresholdExceededPayloadSchema = z.object({
  type: z.literal(F_076_COMPETITOR_ALERT_THRESHOLD_EXCEEDED_EVENT),
  tenantId: z.string().min(1),
  competitorId: z.string().min(1),
  direction: z.enum(F_076_COMPETITOR_ALERT_DIRECTION_VOCAB),
  thresholdBps: z
    .number()
    .int()
    .positive()
    .max(F_076_BENCHMARK_DELTA_BPS_MAX),
  observedDeltaBps: z
    .number()
    .int()
    .min(F_076_BENCHMARK_DELTA_BPS_MIN)
    .max(F_076_BENCHMARK_DELTA_BPS_MAX),
  myScore: z
    .number()
    .int()
    .min(F_076_BENCHMARK_SCORE_MIN)
    .max(F_076_BENCHMARK_SCORE_MAX),
  competitorScore: z
    .number()
    .int()
    .min(F_076_BENCHMARK_SCORE_MIN)
    .max(F_076_BENCHMARK_SCORE_MAX),
  exceededAtIso: z.string().datetime({ offset: true }),
  occurredAtIso: z.string().datetime({ offset: true }),
  correlationId: z.string().min(1),
});
export type F076CompetitorAlertThresholdExceededPayload = z.infer<
  typeof F076CompetitorAlertThresholdExceededPayloadSchema
>;

/* -------------------------------------------------------------------------- */
/* Discriminated union — consumers `switch` for exhaustive narrowing          */
/* -------------------------------------------------------------------------- */

export type F076CompetitorMonitoringEventEnvelope =
  | F076CompetitorProfileTrackedPayload
  | F076CompetitorReviewDetectedPayload
  | F076CompetitorBenchmarkComputedPayload
  | F076CompetitorAlertThresholdExceededPayload;

/* -------------------------------------------------------------------------- */
/* Correlation-id derivation                                                  */
/* -------------------------------------------------------------------------- */
// Pins the deterministic correlation root so the reconciler can join across
// `profile_tracked`, `review_detected`, `benchmark_computed`, and
// `alert_threshold_exceeded` for the same tenant + competitor.

const COMPETITOR_CORRELATION_PREFIX = "competitor:";

export function deriveCompetitorCorrelationId(competitorId: string): string {
  if (typeof competitorId !== "string" || competitorId.length === 0) {
    throw new Error(
      "deriveCompetitorCorrelationId: competitorId must be non-empty string",
    );
  }
  return `${COMPETITOR_CORRELATION_PREFIX}${competitorId}`;
}

/* -------------------------------------------------------------------------- */
/* Pure builders — accept caller primitives, return typed envelope            */
/* -------------------------------------------------------------------------- */

function assertNonEmpty(value: string, field: string): string {
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(`F-076 emitter: ${field} must be non-empty string`);
  }
  return value;
}

function assertNonEmptySources(
  values: readonly F076CompetitorSource[],
): F076CompetitorSource[] {
  if (!Array.isArray(values) || values.length === 0) {
    throw new Error("F-076 emitter: sources must be non-empty array");
  }
  const deduped = Array.from(new Set(values));
  for (const source of deduped) {
    if (!isF076CompetitorSource(source)) {
      throw new Error(
        `F-076 emitter: sources contains unknown value (got ${String(source)})`,
      );
    }
  }
  return deduped;
}

function nowIso(now: Date | undefined): string {
  return (now ?? new Date()).toISOString();
}

export interface BuildCompetitorProfileTrackedInput {
  tenantId: string;
  competitorId: string;
  sources: readonly F076CompetitorSource[];
  displayLabel: string;
  actorUserId: string;
  now?: Date;
}

export function buildCompetitorProfileTrackedEvent(
  input: BuildCompetitorProfileTrackedInput,
): F076CompetitorProfileTrackedPayload {
  return F076CompetitorProfileTrackedPayloadSchema.parse({
    type: F_076_COMPETITOR_PROFILE_TRACKED_EVENT,
    tenantId: assertNonEmpty(input.tenantId, "tenantId"),
    competitorId: assertNonEmpty(input.competitorId, "competitorId"),
    sources: assertNonEmptySources(input.sources),
    displayLabel: assertNonEmpty(input.displayLabel, "displayLabel"),
    actorUserId: assertNonEmpty(input.actorUserId, "actorUserId"),
    occurredAtIso: nowIso(input.now),
    correlationId: deriveCompetitorCorrelationId(input.competitorId),
  });
}

export interface BuildCompetitorReviewDetectedInput {
  tenantId: string;
  competitorId: string;
  platform: F076CompetitorReviewPlatform;
  providerReviewId: string;
  rating: number;
  excerpt: string | null;
  reviewedAtIso: string;
  detectedAtIso: string;
  now?: Date;
}

export function buildCompetitorReviewDetectedEvent(
  input: BuildCompetitorReviewDetectedInput,
): F076CompetitorReviewDetectedPayload {
  const trimmedExcerpt =
    input.excerpt === null
      ? null
      : input.excerpt.slice(0, F_076_REVIEW_EXCERPT_MAX_LENGTH);
  return F076CompetitorReviewDetectedPayloadSchema.parse({
    type: F_076_COMPETITOR_REVIEW_DETECTED_EVENT,
    tenantId: assertNonEmpty(input.tenantId, "tenantId"),
    competitorId: assertNonEmpty(input.competitorId, "competitorId"),
    platform: input.platform,
    providerReviewId: assertNonEmpty(
      input.providerReviewId,
      "providerReviewId",
    ),
    rating: input.rating,
    excerpt: trimmedExcerpt,
    reviewedAtIso: assertNonEmpty(input.reviewedAtIso, "reviewedAtIso"),
    detectedAtIso: assertNonEmpty(input.detectedAtIso, "detectedAtIso"),
    occurredAtIso: nowIso(input.now),
    correlationId: deriveCompetitorCorrelationId(input.competitorId),
  });
}

export interface BuildCompetitorBenchmarkComputedInput {
  tenantId: string;
  competitorId: string;
  myScore: number;
  competitorScore: number;
  deltaBps: number;
  computedAtIso: string;
  now?: Date;
}

export function buildCompetitorBenchmarkComputedEvent(
  input: BuildCompetitorBenchmarkComputedInput,
): F076CompetitorBenchmarkComputedPayload {
  // Cross-field invariant: deltaBps sign must agree with the score
  // difference, so emit-sites cannot bake contradictory facts (e.g.,
  // tenant ahead on score but negative delta) into the immutable
  // event log. Equal scores must produce delta=0.
  const scoreDiff = input.myScore - input.competitorScore;
  const deltaSign = Math.sign(input.deltaBps);
  const diffSign = Math.sign(scoreDiff);
  if (deltaSign !== diffSign) {
    throw new Error(
      `F-076 emitter rejected benchmark with inconsistent deltaBps. ` +
        `myScore=${input.myScore}, competitorScore=${input.competitorScore} ` +
        `implies sign=${diffSign} but deltaBps=${input.deltaBps} has ` +
        `sign=${deltaSign}.`,
    );
  }
  return F076CompetitorBenchmarkComputedPayloadSchema.parse({
    type: F_076_COMPETITOR_BENCHMARK_COMPUTED_EVENT,
    tenantId: assertNonEmpty(input.tenantId, "tenantId"),
    competitorId: assertNonEmpty(input.competitorId, "competitorId"),
    myScore: input.myScore,
    competitorScore: input.competitorScore,
    deltaBps: input.deltaBps,
    computedAtIso: assertNonEmpty(input.computedAtIso, "computedAtIso"),
    occurredAtIso: nowIso(input.now),
    correlationId: deriveCompetitorCorrelationId(input.competitorId),
  });
}

export interface BuildCompetitorAlertThresholdExceededInput {
  tenantId: string;
  competitorId: string;
  direction: F076CompetitorAlertDirection;
  thresholdBps: number;
  observedDeltaBps: number;
  myScore: number;
  competitorScore: number;
  exceededAtIso: string;
  now?: Date;
}

export function buildCompetitorAlertThresholdExceededEvent(
  input: BuildCompetitorAlertThresholdExceededInput,
): F076CompetitorAlertThresholdExceededPayload {
  // Cross-field invariant: direction must agree with the sign of
  // observedDeltaBps so notification routing/templates cannot fire on
  // the wrong side. `tenant_ahead` requires a strictly positive delta
  // and `competitor_ahead` a strictly negative delta.
  if (input.direction === "tenant_ahead" && input.observedDeltaBps <= 0) {
    throw new Error(
      `F-076 emitter rejected direction=tenant_ahead with ` +
        `observedDeltaBps=${input.observedDeltaBps} — must be > 0.`,
    );
  }
  if (
    input.direction === "competitor_ahead" &&
    input.observedDeltaBps >= 0
  ) {
    throw new Error(
      `F-076 emitter rejected direction=competitor_ahead with ` +
        `observedDeltaBps=${input.observedDeltaBps} — must be < 0.`,
    );
  }
  return F076CompetitorAlertThresholdExceededPayloadSchema.parse({
    type: F_076_COMPETITOR_ALERT_THRESHOLD_EXCEEDED_EVENT,
    tenantId: assertNonEmpty(input.tenantId, "tenantId"),
    competitorId: assertNonEmpty(input.competitorId, "competitorId"),
    direction: input.direction,
    thresholdBps: input.thresholdBps,
    observedDeltaBps: input.observedDeltaBps,
    myScore: input.myScore,
    competitorScore: input.competitorScore,
    exceededAtIso: assertNonEmpty(input.exceededAtIso, "exceededAtIso"),
    occurredAtIso: nowIso(input.now),
    correlationId: deriveCompetitorCorrelationId(input.competitorId),
  });
}
